import * as React from 'react';
import { ChangeEvent, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';

import { emailRegexp } from '../../constants';
import { UseState } from '../../types';
import { highlightTextFragments, toMarkdown } from '../../utils';
import { Checkbox } from '../checkbox/checkbox.component';

import BackgroundItemFile from './images/backgroundItem.svg';
import {
  Container,
  DecorationText,
  BackgroundItem,
  BackgroundItemContainer,
  NewsletterMolecule,
  AgreementContainer,
  Button,
  ButtonContainer,
  Input,
  InputAndButtonContainer
} from './newsletterSection.styled';
import { NewsletterSectionProps } from './newsletterSection.types';

export const NewsletterSection: React.FC<NewsletterSectionProps> = (props: NewsletterSectionProps) => {
  const { t }: TransProps<never> = useTranslation();
  const { checkboxLabel, onAction, className }: NewsletterSectionProps = props;
  const [inputValue, setInputValue]: UseState<string> = useState<string>('');
  const [agreementValue, setAgreementValue]: UseState<boolean> = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted]: UseState<boolean> = useState<boolean>(false);
  const [error, setError]: UseState<string> = useState<string>('');

  const buttonOnClick: () => void = (): void => {
    setIsSubmitted(true);
    if (validation()) {
      onAction(inputValue);
      setTimeout(
        () => {
          setInputValue('');
          setAgreementValue(false);
          setIsSubmitted(false);
          setError('');
        },
        2000
      );
    }
  };

  const validation = (email: string = inputValue, agreement: boolean = agreementValue): boolean => {
    if (emailRegexp.test(email) && agreement) {
      setError('');
      return true;
    } else if (emailRegexp.test(email) && !agreement) {
      setError(t('onarte.ui.newsletterSection.agreementRequired'));
    }  else if (!emailRegexp.test(email) && agreement) {
      setError(t('onarte.ui.newsletterSection.emailRequired'));
    } else {
      setError(t('onarte.ui.newsletterSection.emailAndAgreementRequired'));
    }

    return false;
  };

  const setAgreementValueAction: (value: boolean) => void = (value: boolean): void => {
    setAgreementValue(value);
    if (isSubmitted) {
      validation(inputValue, value);
    }
  };

  const setInputValueAction: (value: string) => void = (value: string): void => {
    setInputValue(value);
    if (isSubmitted) {
      validation(value, agreementValue);
    }
  };

  return (
    <Container className={className}>
      <DecorationText
        dangerouslySetInnerHTML={{
          __html: toMarkdown(highlightTextFragments(t('onarte.ui.newsletterSection.decorationText')))
        }}
      />
      <BackgroundItemContainer>
        <BackgroundItem src={BackgroundItemFile} alt='' />
      </BackgroundItemContainer>
      <NewsletterMolecule>
        <InputAndButtonContainer>
          <Input
            value={inputValue}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setInputValueAction(e.target.value)}
            placeholder={t('onarte.ui.newsletterSection.inputPlaceholder')}
          />
          <ButtonContainer>
            <Button onClick={buttonOnClick}>
              {t('onarte.ui.newsletterSection.buttonLabel')}
            </Button>
          </ButtonContainer>
        </InputAndButtonContainer>
        <AgreementContainer>
          <Checkbox
            label={checkboxLabel}
            onChange={setAgreementValueAction}
            validationMessage={error}
            checked={agreementValue}
            id='privacyPolicy'
          />
        </AgreementContainer>
      </NewsletterMolecule>
    </Container>
  );
};
